import type { FC, PropsWithChildren, ReactNode } from "react";
import { TreeItem, TreeItemContent } from "react-aria-components";
import { tv } from "tailwind-variants";

//
// Main criteria node component.
//

interface AssessmentCriteriaNodeProps {
  className?: string;
  textValue: string;
  onPress: (() => void) | undefined;
  children?: ReactNode;
}

const criteriaItemStyle = tv({
  base: `
    group
    col-span-full
    grid
    grid-cols-subgrid
    items-center
    border-t
    border-r
    border-l
    border-border-light
    bg-white
    pr-3
    pl-4
    first:rounded-t-lg
    last:rounded-b-lg
    last:border-b
    data-[react-aria-pressable]:cursor-pointer
    data-[react-aria-pressable]:hover:bg-blue-50
  `,
});

export const AssessmentCriteriaNode: FC<AssessmentCriteriaNodeProps> = (
  props,
) => {
  const { className, textValue, onPress, children } = props;
  return (
    <TreeItem
      textValue={textValue || "Criteria"}
      className={criteriaItemStyle({
        className,
      })}
      onAction={onPress}
    >
      <TreeItemContent>{children}</TreeItemContent>
    </TreeItem>
  );
};

//
// Title component.
//

interface AssessmentCriteriaNodeTitleProps {
  className?: string;
  children: ReactNode;
}

const titleStyle = tv({
  base: `
    truncate
    py-2
    group-aria-[level=2]:pl-5
    group-aria-[level=3]:pl-10
    group-aria-[level=4]:pl-14
  `,
});

export const AssessmentCriteriaNodeTitle: FC<
  AssessmentCriteriaNodeTitleProps
> = (props) => {
  return (
    <div
      className={titleStyle({
        className: props.className,
      })}
    >
      {props.children || "Criteria"}
    </div>
  );
};

//
// Grading component.
//

interface AssessmentCriteriaNodeGradingProps {
  align?: "end";
  children: ReactNode;
}

const gradingStyle = tv({
  base: `
    flex
    py-2
  `,
  variants: {
    align: {
      end: `justify-end`,
    },
  },
});

export const AssessmentCriteriaNodeGrading: FC<
  AssessmentCriteriaNodeGradingProps
> = (props) => {
  const { align, children } = props;
  return (
    <div className={gradingStyle({ align, className: "overflow-hidden" })}>
      {children}
    </div>
  );
};

//
// Commentary component.
//

export const AssessmentCriteriaNodeCommentary: FC<PropsWithChildren> = (
  props,
) => {
  const { children } = props;
  return (
    <div className="flex items-center justify-between gap-x-6 py-2">
      {children}
    </div>
  );
};
